import { type MouseInputAdapter } from '@databases/selections';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';

import {
  createDomMouseInputAdapter,
  type HitTargetResolver,
  parseGridCoord,
  resolveCellCoordinates,
  resolveLayoutShell,
} from './mouse-input-adapters/dom-mouse-input-adapter.ts';
import { isPointerOnDatabaseScrollableNativeScrollbar } from './native-scrollbar-hit.ts';

const resolvePassiveTarget: HitTargetResolver = (element, clientX, clientY) =>
  isPointerOnDatabaseScrollableNativeScrollbar(element, clientX, clientY) ||
  element.closest(
    'button, [role="button"], input, textarea, a, [contenteditable]:not([contenteditable="false"]), [data-database-selection-preserve="true"], [data-testid="layout-right-side-panel"]',
  )
    ? { type: 'none' }
    : null;

const resolveCell: HitTargetResolver = (element) => {
  const root = element.closest(`[data-dbs-layout="${TABLE_V2_LAYOUT_ID}"]`);
  const cell = element.closest('[data-row-index][data-col-index]');

  return root && cell && root.contains(cell) ? resolveCellCoordinates(cell) : null;
};

/**
 * Temporary click targets for verifying V2 highlight coverage before dedicated
 * row/column controls land. Keep this resolver removable; the renderer only
 * publishes indices and the existing mouse input owns selection transitions.
 */
const resolveTemporaryChromeSelection: HitTargetResolver = (element) => {
  const root = element.closest(`[data-dbs-layout="${TABLE_V2_LAYOUT_ID}"]`);
  const chrome = element.closest(
    '[role="columnheader"], [data-testid="table-v2-row-gutter"]',
  );

  if (!root || !chrome || !root.contains(chrome)) {
    return null;
  }

  if (chrome.getAttribute('role') === 'columnheader') {
    const col = parseGridCoord(chrome.getAttribute('data-col-index'));

    return col ? { type: 'column', col } : { type: 'none' };
  }

  const row = parseGridCoord(chrome.getAttribute('data-row-index'));

  return row ? { type: 'row', row, source: 'row-handle' } : { type: 'none' };
};

const hitTargetResolvers = [
  resolvePassiveTarget,
  resolveTemporaryChromeSelection,
  resolveCell,
  resolveLayoutShell,
];

/**
 * Composes V2 targets with the shared DOM adapter. Interactive controls stay
 * passive; cell and temporary chrome targets feed the existing selection loop.
 */
export function createTableV2MouseInputAdapter(): MouseInputAdapter {
  return createDomMouseInputAdapter(hitTargetResolvers);
}
